/* Copyright (c) 2023-2025 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useReducer, useEffect } from "react";
import { ModalConfirm } from "@nordcloud/gnui";
import { SidebarSelector } from "~/components";
import { Item } from "~/components/SidebarSelector/types";
import { showSuccess } from "~/services/toast";
import { generateActionSuccessText } from "~/tools";
import { useGetAllResourceGroups } from "~/views/resourceGroups/hooks/useGetResourceGroups/useGetAllResourceGroups";
import { useUpdateResourceGroupInResource } from "~/views/resourceGroups/hooks/useUpdateResourceGroup/useUpdateResourceGroupInResource";

type State = {
  isAddModalOpen: boolean;
  groupItem: Item;
};

type Props = {
  resourcesIds: string[];
  isOpen: boolean;
  close: () => void;
};

export function AddToResourceGroup({ resourcesIds, isOpen, close }: Props) {
  const [state, updateState] = useReducer(
    (data: State, partialData: Partial<State>) => ({
      ...data,
      ...partialData,
    }),
    { isAddModalOpen: false, groupItem: { label: "", value: "" } }
  );

  const { getGroups, resourceGroups, loading } = useGetAllResourceGroups({
    basic: false,
  });

  const successMessage = (amount: number) => {
    if (amount === 1) {
      return showSuccess(
        generateActionSuccessText("Resource Group")()("updated")()
      );
    } else {
      showSuccess(
        generateActionSuccessText(`${resourcesIds.length} Resources Group`)()(
          "updated"
        )()
      );
    }
  };

  const callback = () => {
    updateState({ isAddModalOpen: false });
    close();
    successMessage(resourcesIds.length);
  };

  const { updateGroup } = useUpdateResourceGroupInResource({
    onSuccess: callback,
  });

  useEffect(() => {
    if (isOpen) {
      getGroups();
    }
  }, [isOpen, getGroups]);

  const options = resourceGroups.map((item) => ({
    label: item?.name ?? "",
    value: item?.id ?? "",
  }));

  const handleSubmit = (item: Item) => {
    updateState({ groupItem: item, isAddModalOpen: true });
  };

  const prepareData = (resources: string[]) => {
    const resourceGroup = resourceGroups.find(
      (group) => group.id === state.groupItem.value
    );
    const resourceSelectors = resourceGroup?.resourceSelectors ?? [];

    const maxResourceSelectorOrder = resourceSelectors
      ? Math.max(
          ...resourceSelectors.map(
            (resourceSelector) => resourceSelector.order
          ),
          0
        )
      : 0;

    return {
      id: state.groupItem.value,
      resourceSelectors: resources.map((item, index) => {
        return {
          resourceId: item,
          order: maxResourceSelectorOrder + index + 1,
        };
      }),
    };
  };

  const handleAddToGroup = () => {
    updateGroup(prepareData(resourcesIds));
  };

  return (
    <>
      <SidebarSelector
        isOpen={isOpen}
        onClick={close}
        onSubmit={handleSubmit}
        options={options}
        loading={loading}
        sideBarTitle="Select Resource Group"
        itemsTitle="Resource Group Name / ID"
      />
      <ModalConfirm
        onClose={() => updateState({ isAddModalOpen: false })}
        isOpen={state.isAddModalOpen}
        confirm={handleAddToGroup}
        actionLabel="Add"
        contentLabel="Add Resource to Resource Group"
      >
        Are you sure you want to add the selected resources to{" "}
        {state.groupItem.label}?
      </ModalConfirm>
    </>
  );
}
